import { Suspense, useRef, useEffect, type ComponentType } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster as ShadcnToaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { ScrollToTop } from "@/components/ScrollToTop";
import { CoperturaApertura } from "@/components/CoperturaApertura";
import ForceUpdateGate from "@/components/ForceUpdateGate";
import { AuthProvider } from "@/contexts/AuthContext";
import { AppLayout } from "@/components/layout/AppLayout";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import PageLoader from "@/components/PageLoader";
import { TransitionVariant } from "@/lib/pageTransitionVariants";
import { useDisableSwipeBack } from "@/hooks/useDisableSwipeBack";

// Lazy load con retry (robusto in WebView) + registrazione per il preload.
// I chunk registrati vengono precaricati quando l'app è ferma, così la
// navigazione tra le pagine non deve più scaricare nulla al momento.
const preloaders: Array<() => Promise<unknown>> = [];
function page<P>(importer: () => Promise<{ default: ComponentType<P> }>, preload = true) {
  if (preload) preloaders.push(importer);
  return lazyWithRetry(importer);
}

const Home = page(() => import("./pages/Home"));
const Ricompense = page(() => import("./pages/Ricompense"));
const RewardDetail = page(() => import("./pages/RewardDetail"));
const MieiRiscatti = page(() => import("./pages/MieiRiscatti"));
const Login = page(() => import("./pages/Login"));
const Register = page(() => import("./pages/Register"));
const ForgotPassword = page(() => import("./pages/ForgotPassword"));
const ResetPassword = page(() => import("./pages/ResetPassword"));
const OffertaDetail = page(() => import("./pages/OffertaDetail"));
const Notifiche = page(() => import("./pages/Notifiche"));
const NotFound = page(() => import("./pages/NotFound"));
const DatiPersonali = page(() => import("./pages/DatiPersonali"));
const LoginSicurezza = page(() => import("./pages/LoginSicurezza"));
const ModificaEmail = page(() => import("./pages/ModificaEmail"));
const ModificaTelefono = page(() => import("./pages/ModificaTelefono"));
const ModificaPassword = page(() => import("./pages/ModificaPassword"));
const TerminiCondizioni = page(() => import("./pages/TerminiCondizioni"));
const PrivacyPolicy = page(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = page(() => import("./pages/CookiePolicy"));
const EliminaAccount = page(() => import("./pages/EliminaAccount"));
// Strumento interno: niente preload, lo apre solo l'admin.
const DiagnosticaPush = page(() => import("./pages/DiagnosticaPush"), false);
const Profilo = page(() => import("./pages/Profilo"));
const Volantino = page(() => import("./pages/Volantino"));
const Punti = page(() => import("./pages/Punti"));
const PuntiExtra = page(() => import("./pages/PuntiExtra"));
const PuntiExtraDettaglio = page(() => import("./pages/PuntiExtraDettaglio"));
const Concierge = page(() => import("./pages/Concierge"));
const Appuntamento = page(() => import("./pages/Appuntamento"));
const NativeCallback = page(() => import("./pages/NativeCallback"));
// Pagine admin: solo retry, niente preload (le usa solo l'admin, chunk pesanti).
const AdminLayout = page(() => import("./pages/admin/AdminLayout"), false);
const AdminPanoramica = page(() => import("./pages/admin/Panoramica"), false);
const AdminPunti = page(() => import("./pages/admin/Punti"), false);
const AdminRiscatti = page(() => import("./pages/admin/Riscatti"), false);
const AdminPromo = page(() => import("./pages/admin/Promo"), false);
const AdminVolantini = page(() => import("./pages/admin/Volantini"), false);
// Pagina di diagnosi temporanea: verifica se la fotocamera funziona dentro Despia.
const AdminUtenti = page(() => import("./pages/admin/Utenti"), false);
const AdminEmail = page(() => import("./pages/admin/Email"), false);
const AdminNissa = page(() => import("./pages/admin/Nissa"), false);
const AdminProvaFotocamera = page(() => import("./pages/admin/ProvaFotocamera"), false);

/** Precarica i chunk delle pagine quando il thread è libero: navigazione senza attese. */
function preloadPages() {
  if (typeof window === "undefined") return;
  const run = () => {
    for (const p of preloaders) p().catch(() => {});
  };
  const ric = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void })
    .requestIdleCallback;
  if (ric) ric(run, { timeout: 4000 });
  else setTimeout(run, 1500);
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 2,
    },
  },
});

// ─── Depth map ────────────────────────────────────────────────────────────────
const DEPTH_MAP: Array<{ pattern: RegExp; depth: number }> = [
  { pattern: /^\/$/, depth: 0 },
  { pattern: /^\/login/, depth: 0 },
  { pattern: /^\/register/, depth: 0 },
  { pattern: /^\/forgot-password/, depth: 0 },
  { pattern: /^\/reset-password/, depth: 0 },
  { pattern: /^\/profilo/, depth: 1 },
  { pattern: /^\/concierge/, depth: 1 },
  { pattern: /^\/appuntamento/, depth: 2 },
  { pattern: /^\/ricompense$/, depth: 1 },
  { pattern: /^\/ricompense\//, depth: 2 },
  { pattern: /^\/miei-riscatti/, depth: 2 },
  { pattern: /^\/notifiche/, depth: 2 },
  { pattern: /^\/dati-personali/, depth: 2 },
  { pattern: /^\/login-sicurezza/, depth: 2 },
  { pattern: /^\/modifica-email/, depth: 3 },
  { pattern: /^\/modifica-telefono/, depth: 3 },
  { pattern: /^\/modifica-password/, depth: 3 },
  { pattern: /^\/termini-condizioni/, depth: 2 },
  { pattern: /^\/privacy-policy/, depth: 2 },
  { pattern: /^\/cookie-policy/, depth: 2 },
  { pattern: /^\/admin$/, depth: 3 },
  { pattern: /^\/admin\//, depth: 4 },
];

function getDepth(pathname: string): number {
  for (const { pattern, depth } of DEPTH_MAP) {
    if (pattern.test(pathname)) return depth;
  }
  return 1;
}

function getTransitionVariant(current: string, previous: string | null): TransitionVariant {
  if (!previous) return 'fade';
  const curr = getDepth(current);
  const prev = getDepth(previous);
  if (curr > prev) return 'forward';
  if (curr < prev) return 'back';
  return 'fade';
}


// ─── Animated Routes ──────────────────────────────────────────────────────────
function AnimatedRoutes() {
  const location = useLocation();
  const prevPath = useRef<string | null>(null);
  useDisableSwipeBack();

  const variant = getTransitionVariant(location.pathname, prevPath.current);
  prevPath.current = location.pathname;

  // Su desktop l'app cliente viene incorniciata in una colonna mobile centrata
  // (.app-scope); la dashboard admin (.admin-scope) resta full-width. Vedi index.css.
  const isAdminRoute = location.pathname.startsWith("/admin");

  return (
    <div className={isAdminRoute ? "admin-scope" : "app-scope"}>
    <RouteErrorBoundary>
    <Suspense fallback={<PageLoader />}>
      <AnimatePresence mode="wait" initial={false} custom={variant}>
        <Routes location={location} key={location.pathname}>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/auth" element={<Navigate to="/login" replace />} />
          <Route path="/native-callback" element={<NativeCallback />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Public routes with layout */}
          <Route path="/" element={<AppLayout><Home /></AppLayout>} />
          <Route path="/volantino" element={<AppLayout><Volantino /></AppLayout>} />
          <Route path="/offerta/:slug" element={<OffertaDetail />} />
          <Route path="/concierge" element={<AppLayout><Concierge /></AppLayout>} />
          <Route path="/appuntamento" element={<Appuntamento />} />
          <Route path="/dati-personali" element={<DatiPersonali />} />
          <Route path="/login-sicurezza" element={<LoginSicurezza />} />
          <Route path="/modifica-email" element={<ModificaEmail />} />
          <Route path="/modifica-telefono" element={<ModificaTelefono />} />
          <Route path="/modifica-password" element={<ModificaPassword />} />
          <Route path="/termini-condizioni" element={<TerminiCondizioni />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/elimina-account" element={<EliminaAccount />} />
          <Route path="/notifiche" element={
            <ProtectedRoute><Notifiche /></ProtectedRoute>
          } />

          {/* Protected routes */}
          <Route path="/ricompense" element={
            <ProtectedRoute><AppLayout><Ricompense /></AppLayout></ProtectedRoute>
          } />
          <Route path="/punti" element={
            <ProtectedRoute><AppLayout><Punti /></AppLayout></ProtectedRoute>
          } />
          <Route path="/punti-extra" element={
            <ProtectedRoute><AppLayout><PuntiExtra /></AppLayout></ProtectedRoute>
          } />
          <Route path="/punti-extra/invita" element={
            <ProtectedRoute><AppLayout><PuntiExtraDettaglio /></AppLayout></ProtectedRoute>
          } />
          <Route path="/ricompense/:id" element={
            <ProtectedRoute><RewardDetail /></ProtectedRoute>
          } />
          <Route path="/profilo" element={
            <ProtectedRoute><AppLayout><Profilo /></AppLayout></ProtectedRoute>
          } />
          <Route path="/miei-riscatti" element={
            <ProtectedRoute><AppLayout><MieiRiscatti /></AppLayout></ProtectedRoute>
          } />

          <Route path="/diagnostica-push" element={
            <ProtectedRoute requireAdmin><DiagnosticaPush /></ProtectedRoute>
          } />

          {/* Admin routes — outside AppLayout (mobile customer shell) */}
          <Route
            path="/admin"
            element={<ProtectedRoute requireAdmin><AdminLayout /></ProtectedRoute>}
          >
            <Route index element={<AdminPanoramica />} />
            <Route path="punti" element={<AdminPunti />} />
            <Route path="riscatti" element={<AdminRiscatti />} />
            <Route path="promo" element={<AdminPromo />} />
            <Route path="volantini" element={<AdminVolantini />} />
            <Route path="utenti" element={<AdminUtenti />} />
            <Route path="email" element={<AdminEmail />} />
            <Route path="nissa" element={<AdminNissa />} />
            <Route path="prova-fotocamera" element={<AdminProvaFotocamera />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </AnimatePresence>
    </Suspense>
    </RouteErrorBoundary>
    </div>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────
const App = () => {

  // Scalda i chunk delle pagine appena l'app è pronta: la navigazione diventa
  // istantanea invece di scaricare ogni pagina al primo accesso.
  useEffect(() => {
    preloadPages();
  }, []);

  // Nessuno splash lato web: allo start ci pensa lo splash nativo di Despia.
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BrowserRouter>
          <AuthProvider>
            <Sonner />
            <ShadcnToaster />
            <ScrollToTop />
            <ForceUpdateGate />
            <AnimatedRoutes />
            <CoperturaApertura />
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
